import { LOCATION_CHANGE } from "connected-react-router";
import { cloneDeep } from "lodash";
import { actionTypes } from "redux-query";
import { WidgetMode, InspectDataPeriods, DetailsPanelType, FromPeriods, LastPeriods, TimePeriods, TenorOptions } from "./types";
import { initialDashboardState } from ".";
import { DatapointIdStandardEnum } from "api/ux/src";
import { dashboardPageTypeLocationPaths } from "components/workspaces/hooks/use-get-dashboards-by-type";
import { getShortLabelForTenor } from "components/widgets/chart/base/utils";
import { widgetNameToWidgetType, WidgetType } from "components/modal/widget-workshop/types";
const getDefaultInspectDataPeriod = (inspectData) => (inspectData?.variation === true
    ? InspectDataPeriods.VariationPeriod
    : InspectDataPeriods.CurrentPeriod);
const isTenorMeasure = (measure) => measure.standardDatapoint === DatapointIdStandardEnum.FundTWR || measure.standardDatapoint === DatapointIdStandardEnum.FundNetIRR;
const applyDefaultTenor = (componentProps, status) => {
    if (status.defaultTenor && componentProps.datapointParams) {
        componentProps.datapointParams[0].measures = componentProps.datapointParams[0]?.measures?.map((measure) => {
            if (isTenorMeasure(measure)) {
                return { ...measure, defaultTenor: status.defaultTenor };
            }
            return measure;
        });
    }
};
export const applyWidgetStatus = (widget, status) => {
    if (!status) {
        return widget;
    }
    const updatedWidget = cloneDeep(widget);
    const componentProps = updatedWidget.componentProps;
    if (componentProps) {
        if (componentProps.userSettings) {
            Object.assign(componentProps.userSettings, {
                customDateRangeEndDate: status.customDateRangeEndDate,
                customDateRangeStartDate: status.customDateRangeStartDate,
                dataGranularity: status.dataGranularity,
                dataGranularityTitle: status.dataGranularityTitle,
                dataGranularityType: status.dataGranularityType,
                dataScale: status.dataScale,
                dateSettingsDefaultValues: status.dateSettingsDefaultValues || {},
                defaultTenor: status.defaultTenor,
                displayOptions: status.displayOptions,
                displayValueType: status.displayValueType,
                excludeSoldAssets: status.excludeSoldAssets,
                gridSwitch: status.gridSwitch,
                hasDatePicker: status.hasDatePicker,
                shownValues: status.shownValues,
                sortBy: status.sortBy,
                viewForm: status.viewForm,
                viewFormExclude: status.viewFormExclude
            });
        }
        componentProps.subtitle = getShortLabelForTenor(status.defaultTenor);
        applyDefaultTenor(componentProps, status);
        componentProps.selectedValues = status.selectedValues;
        componentProps.chartType = status.chartType ?? componentProps.chartType;
        if (componentProps?.userSettings?.chartTypeAvailable && componentProps?.mode === WidgetMode.Normal) {
            componentProps.chartSeriesTypes = status.chartSeriesTypes ?? componentProps.chartSeriesTypes;
        }
    }
    return updatedWidget;
};
export const isViewFormSettingAllowed = (chartType, componentName) => {
    const widgetTypeFromChartType = chartType
        ? widgetNameToWidgetType.get(chartType)
        : undefined;
    const widgetTypeFromComponentName = componentName
        ? widgetNameToWidgetType.get(componentName)
        : undefined;
    const isDonutWidget = widgetTypeFromChartType === WidgetType.Donut;
    const isKpiWidget = widgetTypeFromComponentName === WidgetType.KPI;
    const isSurfaceWidget = widgetTypeFromChartType === WidgetType.Surface || widgetTypeFromComponentName === WidgetType.Surface;
    return !isDonutWidget && !isKpiWidget && !isSurfaceWidget;
};
export const getViewFormSetting = (componentProps, componentName) => {
    if (isViewFormSettingAllowed(componentProps.chartType, componentName)) {
        return { viewForm: componentProps.valueFormat };
    }
    return {};
};
export const getDefaultValues = (componentProps, asOfDate) => {
    const defaults = componentProps.userSettings?.dateSettingsDefaultValues;
    const endDate = asOfDate;
    const startDate = new Date(new Date(endDate).setMonth(endDate.getMonth() - 3));
    return {
        datePickerEndDate: endDate,
        datePickerStartDate: startDate,
        fromPeriod: FromPeriods.Inception,
        lastPeriod: LastPeriods.Months,
        lastPeriodStep: 3,
        selectedRadio: TimePeriods.SpecifiedPeriodToAsOfDate,
        ...defaults
    };
};
const setSelectedValues = (componentProps, widgetStatus, widgetId) => {
    if (componentProps.dataSelectProps) {
        // If widgets has selectbox config, create corresponding sections in status
        widgetStatus[widgetId].selectedValues = componentProps.dataSelectProps
            .reduce((map, obj) => {
            const defaultValues = Object.entries(obj.selectValues).filter(([, value]) => value.defaultValue)
                .map(([key]) => key);
            if (defaultValues.length) {
                [map[obj.order || 0]] = defaultValues;
            }
            else {
                [map[obj.order || 0]] = Object.keys(obj.selectValues);
            }
            return map;
        }, {});
    }
};
export const initSectionStatus = (section, referenceDate) => {
    const sectionStatus = { includeHistoricalCashflows: true, includePacingPlan: false, selectedGroupedWidget: {}, widgets: {} }, { selectedGroupedWidget, widgets: widgetStatus } = sectionStatus;
    // Loop on section widgets
    section?.widgets && widgetStatus && selectedGroupedWidget && section?.widgets.forEach((widget) => {
        const widgetId = widget.id || "";
        const { componentProps } = widget ?? {};
        if (componentProps?.default) {
            selectedGroupedWidget[widgetId] = componentProps.default;
        }
        if (componentProps) {
            widgetStatus[widgetId] = { ...componentProps.userSettings,
                chartSeriesTypes: componentProps.chartSeriesTypes,
                chartType: componentProps.mode === WidgetMode.Comparison ? componentProps.comparisonChartType : componentProps.chartType,
                customDateRangeEndDate: componentProps.userSettings?.customDateRangeEndDate
                    ? new Date(componentProps.userSettings.customDateRangeEndDate)
                    : undefined,
                customDateRangeStartDate: componentProps.userSettings?.customDateRangeStartDate
                    ? new Date(componentProps.userSettings.customDateRangeStartDate)
                    : undefined,
                inspectDataVisiblePeriods: getDefaultInspectDataPeriod(componentProps.inspectData),
                masterDetailsChoice: "",
                periodOfAnalysis: componentProps.userSettings?.periodOfAnalysis?.defaultValue,
                ...getViewFormSetting(componentProps, widget.componentName),
                dateSettingsDefaultValues: getDefaultValues(componentProps, referenceDate || new Date()),
                defaultTenor: componentProps.defaultTenor ?? componentProps.userSettings?.defaultTenor
                    ?? (componentProps.datapointParams?.[0]?.measures?.some(isTenorMeasure) ? TenorOptions.Itd : undefined) };
            setSelectedValues(componentProps, widgetStatus, widgetId);
        }
    });
    return sectionStatus;
};
export const initDashboard = (state, requestedId, investorId) => {
    const investor = investorId;
    const enable = state.dashboardOptions?.enabled;
    if (state.currentEntityId !== requestedId) {
        state.currentEntityId = requestedId;
        delete state.dashboardOptions;
        if (investor) {
            state.dashboardOptions = { enabled: enable, selectedInvestorId: investor };
        }
    }
};
const PANELS_TO_CLOSE_ON_NAVIGATION = [
    DetailsPanelType.CffChangeModal
];
export const closeDetailPanels = (state) => {
    if (state.detailPanels) {
        PANELS_TO_CLOSE_ON_NAVIGATION.forEach((panelType) => {
            const panel = state.detailPanels?.[panelType];
            if (panel?.isOpen) {
                panel.isOpen = false;
            }
        });
    }
};
export const getClearedDashboardState = (state) => {
    const { referenceDate } = state;
    const cloned = cloneDeep(initialDashboardState);
    if (cloned.detailPanels) {
        const DocumentServicefiltersPanel = cloned.detailPanels[DetailsPanelType.DocumentServicefilters];
        if (DocumentServicefiltersPanel) {
            cloned.detailPanels[DetailsPanelType.DocumentServicefilters] = {};
            return { ...state, detailPanels: cloned.detailPanels };
        }
    }
    return state.currentEntityId ? Object.assign(cloned, { isManualChangeDate: false, referenceDate }) : { ...state };
};
export const setUnSelectedLegends = (state, payload) => {
    const { sectionId, widgetId, legendName, selected } = payload;
    if (!state.currentStatus.sections) {
        return;
    }
    if (!state.currentStatus.sections[sectionId]) {
        state.currentStatus.sections[sectionId] = { selectedGroupedWidget: {}, widgets: {} };
    }
    const { widgets } = state.currentStatus.sections[sectionId];
    if (!widgets) {
        return;
    }
    if (!widgets[widgetId]) {
        widgets[widgetId] = {};
    }
    const currentWidgetStatus = widgets[widgetId];
    if (selected) {
        currentWidgetStatus.unSelectedLegends = currentWidgetStatus.unSelectedLegends?.filter((name) => name !== legendName);
    }
    else if (currentWidgetStatus.unSelectedLegends) {
        currentWidgetStatus.unSelectedLegends.push(legendName);
    }
    else {
        currentWidgetStatus.unSelectedLegends = [legendName];
    }
};
const setCurrentStatusSelectedForChildren = (state, parentPayload, config, currentValues, setCurrentStatusFunc) => {
    for (const child of config.filter((s) => s.childOf?.some((c) => c === parentPayload.order))) {
        const currentValue = currentValues[child.order];
        if (parentPayload.forbiddenChildGroups) {
            const allowedValues = Object.entries(child.selectValues)
                .filter(([a, v]) => parentPayload.selectedValue !== a && !parentPayload.forbiddenChildGroups?.includes(v.groupName || ""))
                .map(([k]) => k);
            if (allowedValues.length && !allowedValues.includes(currentValue)) {
                setCurrentStatusFunc(state, { ...parentPayload, order: child.order, selectedValue: allowedValues[0] });
            }
        }
    }
};
export const setCurrentStatusSelection = (state, payload) => {
    const { sectionId, widgetId, order, selectedValue, dataSelectProps, childrenId } = payload, widgetStatus = state.currentStatus.sections?.[sectionId]?.widgets?.[widgetId], widgetStatusChild = state.currentStatus.sections?.[sectionId]?.widgets?.[childrenId || ""];
    if (widgetStatus?.selectedValues) {
        if (widgetStatusChild?.selectedValues) {
            widgetStatusChild.selectedValues[order] = selectedValue;
            if (order === 0 || order === 1) {
                widgetStatus.masterDetailsChoice = "";
                widgetStatusChild.masterDetailsChoice = "";
            }
        }
        widgetStatus.selectedValues[order] = selectedValue;
        if (dataSelectProps) {
            setCurrentStatusSelectedForChildren(state, payload, dataSelectProps, widgetStatus.selectedValues, setCurrentStatusSelection);
        }
    }
};
export const isLocationChangeAction = (action) => action.type === LOCATION_CHANGE && action.payload?.location;
export const isDashboardPageAction = (action) => isLocationChangeAction(action) && dashboardPageTypeLocationPaths.some((page) => action.payload?.location?.pathname?.startsWith(page));
export const isFundResponseReduxQueryAction = (action) => action.type === actionTypes.REQUEST_SUCCESS &&
    Boolean(action.entities?.Fund) &&
    Boolean(Object.keys(action.entities.Fund)[0]) &&
    Boolean(action.entities.Fund[Object.keys(action.entities.Fund)[0]].referenceDate);
export const isFundSeriesResponseReduxQueryAction = (action) => action.type === actionTypes.REQUEST_SUCCESS &&
    Boolean(action.entities?.FundSeries) &&
    Boolean(Object.keys(action.entities.FundSeries)[0]) &&
    Boolean(action.entities.FundSeries[Object.keys(action.entities.FundSeries)[0]].referenceDate);
export const isUserPreferenceSettingReduxQueryPutAction = (action) => action.type === actionTypes.MUTATE_SUCCESS &&
    action.queryKey.startsWith("PutUserPreferences-");
export const isPortfolioResponseReduxQueryAction = (action) => action.type === actionTypes.REQUEST_SUCCESS &&
    Boolean(action.entities?.Portfolio) &&
    Boolean(Object.keys(action.entities.Portfolio)[0]) &&
    Boolean(action.entities.Portfolio[Object.keys(action.entities.Portfolio)[0]].referenceDate);
export const isManagerResponseReduxQueryAction = (action) => action.type === actionTypes.REQUEST_SUCCESS &&
    Boolean(action.entities?.Manager) &&
    Boolean(Object.keys(action.entities.Manager)[0]) &&
    Boolean(action.entities.Manager[Object.keys(action.entities.Manager)[0]].referenceDate);
export const isAssetResponseReduxQueryAction = (action) => action.type === actionTypes.REQUEST_SUCCESS &&
    Boolean(action.entities?.Asset) &&
    Boolean(Object.keys(action.entities.Asset)[0]) &&
    Boolean(action.entities.Asset[Object.keys(action.entities.Asset)[0]].referenceDate);
export const isMarketResponseReduxQueryAction = (action) => action.type === actionTypes.REQUEST_SUCCESS &&
    Boolean(action.entities?.Market) &&
    Boolean(Object.keys(action.entities.Market)[0]) &&
    Boolean(action.entities.Market[Object.keys(action.entities.Market)[0]].referenceDate);
export const GetTemplateByTypeQueryKey = "GetTemplateByType";
export const GetSectionsWidgetsIdQueryKey = "GetSectionWidgetsId";
export const isDashboardChangedAction = (action) => ((action.queryKey?.includes(GetTemplateByTypeQueryKey) || action.queryKey?.includes(GetSectionsWidgetsIdQueryKey)) &&
    action.type === actionTypes.REQUEST_SUCCESS) ||
    (isLocationChangeAction(action) && dashboardPageTypeLocationPaths.some((page) => action.payload?.location?.pathname?.startsWith(page))) ||
    isPortfolioResponseReduxQueryAction(action) || isManagerResponseReduxQueryAction(action) || isFundSeriesResponseReduxQueryAction(action) ||
    isFundResponseReduxQueryAction(action) || isAssetResponseReduxQueryAction(action);
